import React, { useRef, useState, useEffect } from 'react';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { Media } from '.generated/Feature.CW.model';
import ElearningActivity_Component = Media.ElearningActivity_Component;

const activityStyles = tv({
  slots: {
    wrapper: 'flex items-center justify-center bg-white',
    iframe: 'border-none bg-white',
  },
  variants: {
    isFullscreen: {
      true: {
        wrapper: 'fixed inset-0 z-[9999] h-[100dvh] w-screen bg-black',
        // Grow full screen without breaking the aspect ratio
        iframe: 'w-full h-full max-w-[calc(100vh*(4/3))] max-h-[calc(100vw*(3/4))]',
      },
      false: {
        wrapper: 'relative w-full max-w-[738px] mx-auto aspect-[4/3] overflow-hidden',
        iframe: 'w-full h-full',
      },
    },
  },
  defaultVariants: {
    isFullscreen: false,
  },
});

function ElearningActivity({ fields, params }: ElearningActivity_Component) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isCssFullscreen, setIsCssFullscreen] = useState(false);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const { page } = useSitecore();

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsNativeFullscreen(document.fullscreenElement === wrapperRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!fields) return null;

  const { iframeTitle, iframeSrc } = fields;
  const src = iframeSrc?.value?.href;

  if (!src) {
    // Return authoring helper if page is in editing mode and no activity link is added
    if (page.mode.isEditing) {
      return (
        <div className="p-4 bg-gray-100">
          <p>Enter an external link for the iframe source url to see an activity</p>
          <LinkWrapper field={iframeSrc} />
        </div>
      );
    }
    return null;
  }

  const handleFullscreen = async () => {
    const elem = wrapperRef.current;
    if (!elem) return;

    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
    } else {
      setIsCssFullscreen(true);
    }
  };

  const isFullscreen = isCssFullscreen || isNativeFullscreen;
  const { wrapper, iframe } = activityStyles({ isFullscreen });

  return (
    <div className="component flex justify-center">
      <div className="w-full max-w-[738px] mt-8">
        {params?.allowFullscreen && !isFullscreen && (
          <div className="w-full text-right mb-2 pr-[6%]">
            <button
              onClick={handleFullscreen}
              className="text-blue-700 cursor-pointer font-medium hover:underline"
            >
              Fullscreen
            </button>
          </div>
        )}

        <div ref={wrapperRef} className={wrapper()}>
          {isCssFullscreen && (
            <div className="absolute top-0 right-0 w-full flex justify-end p-3 pt-[max(env(safe-area-inset-top,12px),12px)] shrink-0 z-10">
              <button
                onClick={() => setIsCssFullscreen(false)}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-white shadow-md"
              >
                Close
              </button>
            </div>
          )}

          <iframe
            src={src}
            title={iframeTitle?.value}
            tabIndex={0}
            className={iframe()}
            sandbox="allow-scripts"
            allow="autoplay; fullscreen"
            scrolling="no"
          />
        </div>
      </div>
    </div>
  );
}

export const Default = withStandardComponentWrapper(ElearningActivity);
